import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { X } from "lucide-react";
import { BASE_API_URL } from "store/Me";
import { AdminTable } from "components/shared/AdminTable";
import { Alert } from "components/ui/alert";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";

const SCHEDULES_QUERY_KEY = "site-access-schedules";

// ─── Types (matching actual API, not stale NSwag types) ──────────────────────

interface AccessScheduleEntry {
  id: string;
  time: number;
  length: number;
  dayOfWeek: number | string; // may arrive as integer 0-6 or string "Sunday"…"Saturday"
}

interface AccessScheduleItem {
  id: string;
  name: string;
  entries: AccessScheduleEntry[];
}

interface NewEntry {
  dayOfWeek: number;
  startTime: string; // "HH:mm"
  endTime: string;   // "HH:mm"
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Handles both numeric (0-6) and string ("Sunday"…"Saturday") serialization of DayOfWeek
function normalizeDayOfWeek(value: number | string): number {
  if (typeof value === "number") return value;
  const idx = DAY_NAMES.findIndex((d) => d.toLowerCase() === String(value).toLowerCase());
  return idx >= 0 ? idx : (parseInt(String(value), 10) || 0);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function minutesToTimeStr(minutes: number): string {
  const h = Math.floor(minutes / 60).toString().padStart(2, "0");
  const m = (minutes % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}

function timeStrToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

// Groups sorted day numbers into consecutive runs, returns display strings.
// e.g. [1,2,3,4] → ["Mon–Thu"]   [1,3,5] → ["Mon","Wed","Fri"]
function consecutiveDayRanges(days: number[]): string[] {
  const sorted = [...days].sort((a, b) => a - b);
  const runs: number[][] = [[sorted[0]]];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === sorted[i - 1] + 1) {
      runs[runs.length - 1].push(sorted[i]);
    } else {
      runs.push([sorted[i]]);
    }
  }
  return runs.map((run) =>
    run.length > 1
      ? `${DAY_SHORT[run[0]]}–${DAY_SHORT[run[run.length - 1]]}`
      : DAY_SHORT[run[0]],
  );
}

// Groups entries by their time window, then condenses days into ranges.
// Returns one line per unique time window, e.g. ["Mon–Fri 09:00–17:00", "Sat 10:00–14:00"]
function formatEntries(entries: AccessScheduleEntry[]): string[] {
  const groups = new Map<string, number[]>();
  for (const entry of entries) {
    const key = `${entry.time}_${entry.length}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(normalizeDayOfWeek(entry.dayOfWeek));
  }
  return [...groups.entries()].map(([key, days]) => {
    const [t, l] = key.split("_").map(Number);
    const start = minutesToTimeStr(t);
    const end = minutesToTimeStr(t + l);
    const dayLabel = consecutiveDayRanges(days).join(", ");
    return `${dayLabel}  ${start}–${end}`;
  });
}

// ─── API ─────────────────────────────────────────────────────────────────────

async function fetchSchedules(siteId: string): Promise<AccessScheduleItem[]> {
  const res = await axios.get<AccessScheduleItem[]>(
    `${BASE_API_URL}/api/Sites/${siteId}/accessschedules`,
  );
  return res.data;
}

function useGetSchedules(siteId: string) {
  return useQuery({
    queryKey: [SCHEDULES_QUERY_KEY, siteId],
    queryFn: () => fetchSchedules(siteId),
  });
}

// Converts stored entries back to form rows (one row per day/time entry)
function entriesToFormRows(entries: AccessScheduleEntry[]): NewEntry[] {
  return entries.map((entry) => ({
    dayOfWeek: normalizeDayOfWeek(entry.dayOfWeek),
    startTime: minutesToTimeStr(entry.time),
    endTime: minutesToTimeStr(entry.time + entry.length),
  }));
}

// ─── Schedule Modal (Add + Edit) ─────────────────────────────────────────────

const blankEntry = (): NewEntry => ({ dayOfWeek: 1, startTime: "09:00", endTime: "17:00" });

interface ScheduleModalProps {
  open: boolean;
  siteId: string;
  schedule?: AccessScheduleItem; // present = edit mode
  onClose: (saved?: boolean) => void;
}

function ScheduleModal({ open, siteId, schedule, onClose }: ScheduleModalProps) {
  const isEdit = !!schedule;
  const [name, setName] = useState("");
  const [entries, setEntries] = useState<NewEntry[]>([blankEntry()]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    if (open) {
      setName(schedule?.name ?? "");
      setEntries(
        schedule?.entries?.length
          ? entriesToFormRows(schedule.entries)
          : [blankEntry()],
      );
      setError("");
    }
  }, [open, schedule]);

  const addEntry = () => setEntries((prev) => [...prev, blankEntry()]);

  const removeEntry = (i: number) =>
    setEntries((prev) => prev.filter((_, idx) => idx !== i));

  const updateEntry = (i: number, patch: Partial<NewEntry>) =>
    setEntries((prev) => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError("Name is required."); return; }
    if (entries.length === 0) { setError("Add at least one entry."); return; }

    const payload = {
      name: name.trim(),
      entries: entries.map((entry) => ({
        dayOfWeek: entry.dayOfWeek,
        time: timeStrToMinutes(entry.startTime),
        length: Math.max(0, timeStrToMinutes(entry.endTime) - timeStrToMinutes(entry.startTime)),
      })),
      groups: [],
    };

    setLoading(true);
    setError("");
    try {
      if (isEdit) {
        await axios.post(`${BASE_API_URL}/api/Sites/${siteId}/accessschedules/${schedule!.id}`, payload);
      } else {
        await axios.post(`${BASE_API_URL}/api/Sites/${siteId}/accessschedules`, payload);
      }
      onClose(true);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? "Failed to save schedule.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        className="sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Access Schedule" : "Add Access Schedule"}
          </DialogTitle>
        </DialogHeader>
        <form className="grid gap-4" onSubmit={handleSubmit}>
          {error && <Alert variant="destructive">{error}</Alert>}

          <div className="grid gap-2">
            <Label htmlFor="site-access-schedule-name">Name</Label>
            <Input
              id="site-access-schedule-name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div style={{ fontWeight: 500, marginBottom: 8 }}>Time Entries</div>
          {entries.map((entry, i) => (
            <div
              key={i}
              className="flex flex-wrap items-end gap-2"
            >
              <div className="grid min-w-[130px] gap-2">
                <Label htmlFor={`site-access-schedule-day-${i}`}>Day</Label>
                <Select
                  value={String(entry.dayOfWeek)}
                  onValueChange={(dayOfWeek) =>
                    updateEntry(i, { dayOfWeek: Number(dayOfWeek) })
                  }
                >
                  <SelectTrigger id={`site-access-schedule-day-${i}`} className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAY_NAMES.map((day, index) => (
                      <SelectItem key={day} value={String(index)}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid min-w-[130px] gap-2">
                <Label htmlFor={`site-access-schedule-start-${i}`}>Start</Label>
                <Input
                id={`site-access-schedule-start-${i}`}
                type="time"
                value={entry.startTime}
                  onChange={(event) =>
                    updateEntry(i, { startTime: event.target.value })
                  }
                />
              </div>
              <div className="grid min-w-[130px] gap-2">
                <Label htmlFor={`site-access-schedule-end-${i}`}>End</Label>
                <Input
                id={`site-access-schedule-end-${i}`}
                type="time"
                value={entry.endTime}
                  onChange={(event) =>
                    updateEntry(i, { endTime: event.target.value })
                  }
                />
              </div>
              {entries.length > 1 && (
                <Button
                  aria-label="Remove entry"
                  onClick={() => removeEntry(i)}
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  size="icon-sm"
                  type="button"
                  variant="ghost"
                >
                  <X />
                </Button>
              )}
            </div>
          ))}
          <Button size="sm" type="button" variant="ghost" onClick={addEntry}>
            + Add Entry
          </Button>
          <DialogFooter>
            <Button disabled={loading} onClick={() => onClose()} type="button" variant="outline">
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Save Changes" : "Add Schedule"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Tab Component ────────────────────────────────────────────────────────────

export default function SiteAccessSchedulesTab({ siteId }: { siteId: string }) {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<AccessScheduleItem | undefined>(undefined);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, isLoading, isError } = useGetSchedules(siteId);
  const schedules = data ?? [];

  const handleModalClose = (saved?: boolean) => {
    setModalOpen(false);
    setEditingSchedule(undefined);
    if (saved) queryClient.invalidateQueries({ queryKey: [SCHEDULES_QUERY_KEY, siteId] });
  };

  const openEdit = (schedule: AccessScheduleItem) => {
    setEditingSchedule(schedule);
    setModalOpen(true);
  };

  const openAdd = () => {
    setEditingSchedule(undefined);
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await axios.delete(`${BASE_API_URL}/api/Sites/${siteId}/accessschedules/${id}`);
      queryClient.invalidateQueries({ queryKey: [SCHEDULES_QUERY_KEY, siteId] });
    } catch {
      // silent
    } finally {
      setDeletingId(null);
    }
  };

  if (isError) {
    return (
      <div className="flex min-h-80 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-center text-sm text-rose-700 shadow-sm">
        Error loading access schedules.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-80 items-center justify-center rounded-lg border border-slate-200 bg-white text-sm text-slate-500 shadow-sm">
        Loading access schedules...
      </div>
    );
  }

  return (
    <>
      <AdminTable
        aria-label="Site access schedules"
        toolbar={
          <div className="flex justify-end px-4 py-4">
            <Button size="sm" type="button" onClick={openAdd}>
              Add Schedule
            </Button>
          </div>
        }
      >
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Entries</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {schedules.map((s) => (
            <tr key={s.id}>
              <td style={{ fontWeight: 500 }}>{s.name || "—"}</td>
              <td>
                {s.entries && s.entries.length > 0 ? (
                  formatEntries(s.entries).map((line, i) => (
                    <div key={i} style={{ fontSize: 13 }}>{line}</div>
                  ))
                ) : (
                  <span style={{ color: "gray" }}>No entries</span>
                )}
              </td>
              <td>
                <Button
                  className="mr-1"
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={() => openEdit(s)}
                >
                  Edit
                </Button>
                <Button
                  disabled={deletingId === s.id}
                  onClick={() => handleDelete(s.id)}
                  size="sm"
                  type="button"
                  variant="destructive"
                >
                  {deletingId === s.id ? "Deleting..." : "Delete"}
                </Button>
              </td>
            </tr>
          ))}
          {schedules.length === 0 && (
            <tr>
              <td colSpan={3} className="py-16 text-center text-sm text-slate-500">
                No access schedules found.
              </td>
            </tr>
          )}
        </tbody>
      </AdminTable>
      <ScheduleModal open={modalOpen} siteId={siteId} schedule={editingSchedule} onClose={handleModalClose} />
    </>
  );
}
